import React, { useEffect, useRef, useState } from 'react';

import { Tabs } from 'app-components';
import { TabsRef } from 'app-components/dist/typings/molecules/Tabs';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

import { modalConfig } from './modals/modalConfig';
import { configs } from '@constants/configs/configs';
import HealthPlan from './components/HealthPlan/HealthPlan';
import DentalPlan from './components/DentalPlan/DentalPlan';
import withModalWrapper from '@containers/common/WithModalWrapper';
import { environmentSelector } from '@selectors/commonSelectors';
import { useAppDispatch, useAppSelector, useModal, useStateCode, useLocalStorage } from '@hooks/index';
import { fetchCustomGroupingDetails } from '@actions/individualPortal/customGrouping/customGroupingActionCreators';
import useCustomGrouping from './hooks/useCustomGrouping';
import { CASE_NUMBER, APPLICATION_YEAR } from '@constants/individualPortal/customGrouping/customGrouping.constants';
import { SEP_END_DATE_SESSION_STORAGE_KEY } from '@constants/common/miscellaneous';

const StyledTabsWrapper = styled.div`
    padding: 0px 5px;

    .usa-list.gi-tabs {
        margin-top: unset; /* This is to unset the default margin-top property already defined in gi-tabs class*/
        margin-bottom: 6px;
    }

    .gi-tabs__item {
        flex: 1;
    }
`;

const CustomGroupingContainer = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { isHideDentalAPTCIfNoKids } = useStateCode();
    const { openModal } = useModal();
    const { processCustomGroupingData, calculateRemainingDays } = useCustomGrouping({ dispatch });
    const { getItem } = useLocalStorage();

    const { activeTab, dashboardModels } = useAppSelector(state => state.individualPortal.dashboard);

    const environment = useAppSelector(environmentSelector);
    const { caseNumber:reduxCaseNumber, applicationYear:reduxApplicationYear, sepEndDate } = dashboardModels[activeTab] || {};
    const lsCaseNumber = getItem(CASE_NUMBER);
    const lsAppliationYear = getItem(APPLICATION_YEAR);

    const sepEndDateSessionStorage = sessionStorage.getItem(SEP_END_DATE_SESSION_STORAGE_KEY);

    const applicationYear = reduxApplicationYear || lsAppliationYear;
    const caseNumber = reduxCaseNumber || lsCaseNumber;
    
    const [daysRemaining] = useState(
        calculateRemainingDays({
            oepEndDate: environment.localDates?.oepEndDate,
            sepEndDate: sepEndDate || sepEndDateSessionStorage,
            serverDate: environment.localDates?.serverDate,
        }),
    );

    // Fetch details when caseNumber is available
    useEffect(() => {
        if (!caseNumber) {
            navigate(configs.routes.INDPORTAL);
        } else {
            dispatch(
                fetchCustomGroupingDetails(
                    configs.endpoints.CUSTOM_GROUPING_DETAIL.processReplacements({
                        caseNumber,
                    }),
                    {
                        environment,
                        isHideDentalAPTCIfNoKids,
                        openModal,
                        setActiveTab: tabsRef.current?.setActiveTab,
                        processData: processCustomGroupingData,
                        caseNumber,
                        applicationYear,
                    },
                ),
            );
        }
    }, []);

    const tabsRef = useRef<TabsRef>(null);

    const tabs = [
        {
            title: 'customGrouping.shopHealthPlans',
            titleValue: { coverageYear: applicationYear },
            render: () => <HealthPlan daysRemaining={daysRemaining} data-testid="healthPlan" index={1}/>,
        },
        {
            title: 'customGrouping.shopDentalPlans',
            titleValue: { coverageYear: applicationYear },
            render: () => <DentalPlan setActiveTab={tabsRef.current?.setActiveTab} daysRemaining={daysRemaining} data-testid="dentalPlan" index={2}/>,
        },
    ];

    return (
        <>
            <StyledTabsWrapper id="custom-grouping">
                <Tabs tabs={tabs} ref={tabsRef} />
            </StyledTabsWrapper>
        </>
    );
};

export default withModalWrapper(CustomGroupingContainer, modalConfig);
